import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  ButtonGroup,
  Spinner,
  Banner,
  Box,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { PricingCard, PricingPlan, UsageProgressBar } from '../components/subscription';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface SubscriptionState {
  plans: PricingPlan[];
  currentPlan: string | null;
  usage: {
    used: number;
    limit: number | null;
    periodStart?: Date;
    periodEnd?: Date;
  } | null;
  subscriptionStatus: string | null;
  isLoading: boolean;
  error: string | null;
}

export const Pricing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [state, setState] = useState<SubscriptionState>({
    plans: [],
    currentPlan: null,
    usage: null,
    subscriptionStatus: null,
    isLoading: true,
    error: null,
  });

  const fetchData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Plans endpoint is public; the current-subscription endpoint needs auth
      const plansResponse = await api.subscription.getPlans();

      let currentPlan: string | null = null;
      let usage: SubscriptionState['usage'] = null;
      let subscriptionStatus: string | null = null;

      if (isAuthenticated) {
        try {
          const currentResponse = await api.subscription.getCurrent();
          currentPlan = currentResponse.data.subscription?.plan?.planKey || null;
          usage = currentResponse.data.usage ? {
            used: currentResponse.data.usage.ordersSynced,
            limit: currentResponse.data.usage.ordersLimit,
            periodStart: currentResponse.data.usage.billingPeriodStart,
            periodEnd: currentResponse.data.usage.billingPeriodEnd,
          } : null;
          subscriptionStatus = currentResponse.data.subscription?.status || null;
        } catch (err) {
          console.error('Failed to fetch current subscription:', err);
        }
      }

      setState(prev => ({
        ...prev,
        plans: plansResponse.data.plans,
        currentPlan,
        usage,
        subscriptionStatus,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch pricing data:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load pricing information. Please try again.',
      }));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSelectPlan = async (planId: number) => {
    if (!isAuthenticated) {
      // Prospect viewing public pricing — send them to install
      navigate('/install');
      return;
    }

    setIsProcessing(true);

    try {
      // Create Razorpay order
      const response = await api.subscription.subscribe(planId, billingCycle);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create order');
      }

      const { orderId, amount, razorpayKey, invoiceNumber } = response.data;

      // Open Razorpay checkout
      const options = {
        key: razorpayKey,
        amount: amount,
        currency: 'INR',
        name: 'Shopify Tally Sync',
        description: `${billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription`,
        order_id: orderId,
        handler: async function (razorpayResponse: any) {
          try {
            // Verify payment
            const verifyResponse = await api.subscription.verifyPayment({
              razorpayOrderId: razorpayResponse.razorpay_order_id,
              razorpayPaymentId: razorpayResponse.razorpay_payment_id,
              razorpaySignature: razorpayResponse.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              // Refresh data
              await fetchData();
            } else {
              throw new Error(verifyResponse.data.error || 'Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            setState(prev => ({
              ...prev,
              error: 'Payment verification failed. Please contact support.',
            }));
          }
        },
        prefill: {
          email: '',
        },
        theme: {
          color: '#008060',
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to initiate payment. Please try again.',
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  if (state.isLoading) {
    return (
      <Page title="Pricing">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" blockAlign="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Pricing Plans"
      subtitle="Choose the plan that best fits your business needs"
      backAction={{ content: 'Back', url: isAuthenticated ? '/dashboard' : '/' }}
    >
      <Layout>
        {!isAuthenticated && (
          <Layout.Section>
            <Banner title="Install to subscribe" tone="info">
              <p>
                You're viewing public pricing. Install Shopify Tally Sync on your store to start a
                free 7-day trial and subscribe to a plan.
              </p>
              <Box paddingBlockStart="200">
                <Button onClick={() => navigate('/install')} variant="primary">
                  Install on Shopify
                </Button>
              </Box>
            </Banner>
          </Layout.Section>
        )}

        {state.error && (
          <Layout.Section>
            <Banner
              title="Error"
              tone="critical"
              onDismiss={() => setState(prev => ({ ...prev, error: null }))}
            >
              <p>{state.error}</p>
            </Banner>
          </Layout.Section>
        )}

        {state.subscriptionStatus === 'trialing' && (
          <Layout.Section>
            <Banner title="You're on a free trial" tone="info">
              <p>
                Your trial gives you full access to the Starter plan features.
                Subscribe to a paid plan before your trial ends to continue syncing.
              </p>
            </Banner>
          </Layout.Section>
        )}

        {state.usage && (
          <Layout.Section>
            <UsageProgressBar
              used={state.usage.used}
              limit={state.usage.limit}
              periodStart={state.usage.periodStart}
              periodEnd={state.usage.periodEnd}
            />
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="center">
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={billingCycle === 'monthly'}
                    onClick={() => setBillingCycle('monthly')}
                  >
                    Monthly
                  </Button>
                  <Button
                    pressed={billingCycle === 'yearly'}
                    onClick={() => setBillingCycle('yearly')}
                  >
                    Yearly (Save 20%)
                  </Button>
                </ButtonGroup>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Box paddingBlockStart="400">
            <InlineStack gap="400" align="start" wrap>
              {state.plans.map((plan) => (
                <Box key={plan.id} minWidth="280px" maxWidth="320px">
                  <PricingCard
                    plan={plan}
                    currentPlanKey={state.currentPlan || undefined}
                    billingCycle={billingCycle}
                    onSelect={handleSelectPlan}
                    isLoading={isProcessing}
                  />
                </Box>
              ))}
            </InlineStack>
          </Box>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h3">Frequently Asked Questions</Text>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h4">What happens when I exceed my order limit?</Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  You'll receive a warning at 80% usage. Once you reach 100%, new syncs will be paused.
                  You'll have a 3-day grace period to upgrade before syncing stops completely.
                </Text>
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h4">Can I change my plan later?</Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  Yes! You can upgrade at any time and only pay the prorated difference.
                  Downgrades take effect at the end of your current billing period.
                </Text>
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h4">What payment methods do you accept?</Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  We accept all major credit cards, debit cards, UPI, and net banking through Razorpay.
                </Text>
              </BlockStack>

              <BlockStack gap="200">
                <Text variant="headingSm" as="h4">Is there a refund policy?</Text>
                <Text as="span" variant="bodySm" tone="subdued">
                  We offer a 7-day money-back guarantee for new subscriptions.
                  Contact support within 7 days of your first payment for a full refund.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Pricing;
